<template>
  <div>
    <v-container>
      <div class="view-container">
        <div v-if="mode == 'view'">
          <v-row class="mb-6" @mouseover="overTitle = true" @mouseleave="overTitle = false">
            <v-col cols="auto" class="relative-col cursor-pointer" @click="mode = 'edit'">
              <h2 class="itemis-analyze-title">{{ projectStore.currentProject?.name }}</h2>
              <br />
              <span class="text-caption">{{
                getProjectUrl(projectStore.currentProject?.slug)
              }}</span>
              <v-icon v-if="overTitle" class="edit-icon" @click="mode = 'edit'">mdi-pencil</v-icon>
            </v-col>
          </v-row>
          <v-row
            v-if="false"
            @mouseover="overDescription = true"
            @mouseleave="overDescription = false"
          >
            <v-col cols="auto" class="relative-col ml-1">
              <span class="itemis-analyze-text">
                {{
                  projectStore.currentProject?.description || L.PROJECT_DESCRIPTION_EMPTY_STATE
                }}</span
              >
              <v-icon v-if="overDescription" size="small" class="edit-icon" @click="mode = 'edit'"
                >mdi-pencil</v-icon
              >
            </v-col>
          </v-row>
        </div>
        <v-row v-if="mode == 'edit'">
          <v-col cols="12" md="6" lg="5">
            <v-text-field
              id="project-config-name"
              v-model="projectName"
              class="text-black font-weight-medium"
              variant="outlined"
              :label="L.PROJECT_NAME_LABEL"
              @keyup.enter="putProject"
            ></v-text-field>
            <ProjectSlugInput
              v-model="projectSlug"
              :name="projectName"
              :allowed="currentProject?.slug"
              :organization-id-or-slug="organizationId"
              :loading="organizationIdLoading"
              @keyup.enter="putProject"
            />
            <v-textarea
              v-if="false"
              ref="projectDescriptionField"
              class="text-medium-emphasis"
              :model-value="projectStore.currentProject?.description"
              variant="outlined"
              :label="L.PROJECT_DESCRIPTION_LABEL"
              rows="2"
              max-rows="3"
            ></v-textarea>
            <v-btn
              variant="flat"
              color="cyan-dark"
              prepend-icon="mdi-content-save"
              class="px-8"
              @click="putProject"
              >Save</v-btn
            >
          </v-col>
        </v-row>
      </div>
      <v-row>
        <v-col v-for="(config, index) in cardConfigs" :key="index" cols="12" md="6">
          <ConfigurationCard
            :id="config.id"
            :icon="config.icon"
            :title="config.title"
            :subtitle="config.subtitle"
            :text="config.text"
          >
            <template v-if="config.isEditable" #actions>
              <v-label>{{ config.selectLabel }}</v-label>
              <v-select
                variant="outlined"
                density="compact"
                :items="config.items()"
                :item-title="config.itemTitle"
                :model-value="config.selectModelValue"
                :clearable="config.isClearable"
                :placeholder="config.placeholder"
                @update:model-value="config.updateModelValue"
              ></v-select>
              <v-btn
                :data-testid="config.buttonLabel"
                variant="flat"
                color="cyan-dark"
                prepend-icon="mdi-square-edit-outline"
                class="px-8"
                :to="config.link"
              >
                {{ config.buttonLabel }}
              </v-btn>
            </template>
            <template v-else #actions>
              <v-btn
                variant="flat"
                color="cyan-dark"
                prepend-icon="mdi-square-edit-outline"
                class="px-8"
                :to="config.link"
              >
                {{ config.buttonLabel }}
              </v-btn>
            </template>
          </ConfigurationCard>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script lang="ts" setup>
  // TODO: Implement navigation links to the Snapshots, Global Filters, and Custom Metrics pages once these pages are created.

  import { onMounted, onUnmounted, ref, watch } from 'vue'
  import ConfigurationCard from './ConfigurationCard.vue'
  import { useGlobalFilterStore } from '@/stores/globalFilterStore'
  import { useProjectStore } from '@/stores/projectStore'
  import { useSnapshotStore } from '@/stores/snapshotStore'
  import labels from '@/translations/labels.json'
  import { ResourcesService } from '@itemis-solutions/platform-client-sdk'
  import { ConfigurationCardItem } from '@/models/configuration-card.model'
  import { GlobalFilter, Snapshot } from '@/models'
  import { injectTyped } from '@/inject'
  import { ServiceKeys } from '@/services/symbols'
  import { storeToRefs } from 'pinia'
  import { watchEffect } from 'vue'
  import ProjectSlugInput, {
    getProjectUrl,
  } from '@/components/NewProjectDialog/ProjectSlugInput.vue'
  import { asyncComputed } from '@vueuse/core'

  const L = labels.PROJECT_CONFIGURATION

  export type Mode = 'view' | 'edit'
  const mode = ref<Mode>('view')
  const projectStore = useProjectStore()
  const snapShotStore = useSnapshotStore()
  const globalFilterStore = useGlobalFilterStore()

  const selectedSnapshot = ref<Snapshot | undefined>(snapShotStore.currentSnapshot)
  const selectedGlobalFilter = ref<GlobalFilter | undefined>(globalFilterStore.selectedGlobalFilter)
  const projectService = injectTyped(ServiceKeys.project)

  const { currentProject } = storeToRefs(useProjectStore())
  const projectName = ref(currentProject.value?.name)
  watchEffect(() => (projectName.value = currentProject.value?.name))

  const projectSlug = ref(currentProject.value?.slug)
  watchEffect(() => (projectSlug.value = currentProject.value?.slug))

  const organizationIdLoading = ref(false)
  const organizationId = asyncComputed<string | undefined>(
    () => {
      if (currentProject.value?.resourceId)
        return ResourcesService.getResource({ resourceId: currentProject.value?.resourceId }).then(
          (resp) => resp.data.organization_id,
        )
      else return undefined
    },
    undefined,
    organizationIdLoading,
  )

  const projectDescriptionField = ref<HTMLInputElement>()

  const overTitle = ref(false)
  const overDescription = ref(false)
  const putProject = async () => {
    const project = projectStore.currentProject
    if (project) {
      const p = JSON.parse(JSON.stringify(project))
      p.name = projectName.value
      p.slug = projectSlug.value
      // TODO: Make the description editable
      // p.description = projectDescriptionField.value?.value || project.description
      try {
        if (projectService) {
          await projectStore.putProject(projectService, p)
        }
      } catch (error) {
        console.error(error)
      } finally {
        mode.value = 'view'
      }
    }
  }
  const setSnapshot = (snapshot: Snapshot | undefined) => {
    if (!snapshot) return
    const s = snapShotStore.snapshots.find((snap) => snap.name === snapshot.name)
    snapShotStore.setCurrentSnapshot(s)
    selectedSnapshot.value = s
  }
  const setGlobalFilter = (filter: GlobalFilter | undefined) => {
    if (!filter) return
    const gf = globalFilterStore.globalFilters.find((f) => f.name === filter.name)
    globalFilterStore.applyGlobalFilter(gf)
    selectedGlobalFilter.value = gf
  }

  const cardConfigs = ref<ConfigurationCardItem<any>[]>([
    {
      id: 'snapshots',
      icon: 'mdi-camera-iris',
      title: L.SNAPSHOTS_CARD.TITLE,
      subtitle: L.SNAPSHOTS_CARD.SUBTITLE,
      text: L.SNAPSHOTS_CARD.TEXT,
      items: () => snapShotStore.snapshots,
      itemTitle: 'name',
      selectLabel: L.SNAPSHOTS_CARD.SELECT_LABEL,
      buttonLabel: L.SNAPSHOTS_CARD.BTN_LABEL,
      selectModelValue: selectedSnapshot,
      updateModelValue: setSnapshot,
      isEditable: true,
      isClearable: false,
      link: 'snapshots',
    } as ConfigurationCardItem<Snapshot>,
    {
      id: 'global-filters',
      icon: 'mdi-filter-outline',
      title: L.GLOBAL_FILTERS_CARD.TITLE,
      subtitle: L.GLOBAL_FILTERS_CARD.SUBTITLE,
      text: L.GLOBAL_FILTERS_CARD.TEXT,
      items: () => globalFilterStore.globalFilters,
      itemTitle: 'name',
      selectLabel: L.GLOBAL_FILTERS_CARD.SELECT_LABEL,
      buttonLabel:
        selectedGlobalFilter.value == undefined
          ? L.GLOBAL_FILTERS_CARD.BTN_CREATE_TEXT
          : L.GLOBAL_FILTERS_CARD.BTN_EDIT_TEXT,
      selectModelValue: selectedGlobalFilter,
      updateModelValue: setGlobalFilter,
      isEditable: true,
      isClearable: true,
      placeholder: labels.GLOBAL_FILTER.NO_FILTER_APPLIED,
      link: 'filters',
    } as ConfigurationCardItem<GlobalFilter>,
  ])

  watch(
    () => projectStore.currentProject?.features,
    () => {
      if (
        !cardConfigs.value.find((c) => c.id === 'custom-metrics') &&
        projectStore.isFeatureActive('analyze:custom-metrics')
      ) {
        cardConfigs.value.push({
          id: 'custom-metrics',
          icon: 'mdi-chart-timeline-variant',
          title: L.CUSTOM_METRICS_CARD.TITLE,
          subtitle: L.CUSTOM_METRICS_CARD.SUBTITLE,
          items: () => [],
          text: L.CUSTOM_METRICS_CARD.TEXT,
          buttonLabel: L.CUSTOM_METRICS_CARD.BTN_TEXT,
          isEditable: false,
          isClearable: false,
          link: 'metrics',
        })
      }
    },
    { immediate: true },
  )

  watch(
    () => projectStore.currentProject,
    (n, _) => {
      if (n) globalFilterStore.loadGlobalFiltersFromApi(n?.id)
    },
  )

  watch(
    () => snapShotStore.currentSnapshot,
    (n, old) => {
      if (old == undefined || old == null) selectedSnapshot.value = n
    },
  )

  watch(
    () => globalFilterStore.selectedGlobalFilter,
    (n, _) => {
      selectedGlobalFilter.value = n
    },
  )

  const handleEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (mode.value == 'edit') {
        mode.value = 'view'
      }
    }
  }

  onMounted(() => {
    document.addEventListener('keyup', handleEscape)
  })

  onUnmounted(() => {
    document.removeEventListener('keyup', handleEscape)
  })
</script>

<style scoped>
  .relative-col {
    position: relative;
  }

  .edit-icon {
    cursor: pointer;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: -20px;
  }
</style>
